import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useFormHook from '@shared/hooks/useFormHook';
import { notifyError } from '@config/tostifyConfig/tostifyConfig';
import { art123Service } from '../services/art123Service';

const truncateText = (text, maxLength = 90) => {
    if (!text) return '';
    const normalized = String(text).trim();
    if (normalized.length <= maxLength) return normalized;
    return `${normalized.slice(0, maxLength).trim()}...`;
};

export const useArt123GenerateAgreementForm = ({ onClose, onConfirm }) => {
    const { t } = useTranslation();
    const { modalRef } = useFormHook({}, onClose);
    const [reasons, setReasons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reasonsLoading, setReasonsLoading] = useState(false);

    const validationSchema = yup.object({
        documentDate: yup.string().required(t('common.required')),
        effectiveDate: yup.string().required(t('common.required')),
        reasonId: yup.string().required(t('common.required')),
    });

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            documentDate: '',
            effectiveDate: '',
            reasonId: '',
        },
    });

    const selectedReasonId = watch('reasonId');
    const selectedReason = reasons.find((reason) => String(reason.id) === String(selectedReasonId));

    useEffect(() => {
        const fetchReasons = async () => {
            try {
                setReasonsLoading(true);
                const data = await art123Service.getChangeEmployerReasons();
                setReasons(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching change employer reasons:', error);
                setReasons([]);
                notifyError(error?.response?.data?.message || t('common.errorMessage'));
            } finally {
                setReasonsLoading(false);
            }
        };

        fetchReasons();
    }, [t]);

    const submitForm = async (data) => {
        try {
            setLoading(true);
            if (onConfirm) {
                await onConfirm(data);
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.response?.data || t('common.errorMessage');
            notifyError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const getReasonOptionLabel = (reason) => {
        if (reason.reasonId) {
            return `${reason.reasonId} - ${truncateText(reason.description)}`;
        }
        return truncateText(reason.description);
    };

    return {
        t,
        modalRef,
        reasons,
        loading,
        reasonsLoading,
        register,
        handleSubmit,
        setValue,
        watch,
        errors,
        selectedReason,
        submitForm,
        getReasonOptionLabel,
    };
};
